import { Card, CardTitle } from 'components/Card/Card';
import { Loader2 } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { formatChf, RevenueMonth, RevenueTypeAmount, useRevenueReport, useTypeLabel } from './revenueShared';

const findAmount = (rows: readonly RevenueTypeAmount[], row: RevenueTypeAmount) =>
    rows.find(r => r.key === row.key && (r.parent ?? null) === (row.parent ?? null))?.amount ?? '0';

export const RevenueTable = () => {
    const { t, i18n } = useTranslation('dashboard');
    const typeLabel = useTypeLabel();
    const { data, loading, error } = useRevenueReport();
    const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
    const months = data?.revenueReport.months ?? [];

    const monthName = (m: RevenueMonth) =>
        new Intl.DateTimeFormat(i18n.language?.startsWith('en') ? 'en-GB' : 'de-CH', {
            month: 'long',
            year: 'numeric',
        }).format(new Date(m.year, m.month - 1, 1));

    const toggle = (id: string) =>
        setOpen(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });

    return (
        <Card className="border">
            <CardTitle className="text-base">{t('revenueTableTitle')}</CardTitle>
            <p className="text-sm text-zinc-500 mb-4">{t('revenueTableHint')}</p>
            {loading && <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />}
            {!loading && error && <div className="text-sm text-red-600">{t('revenueError')}</div>}
            {!loading && !error && (
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-zinc-500 border-b border-zinc-200">
                                <th rowSpan={2} className="py-2 pr-4 font-medium align-bottom">
                                    {t('revenueMonth')}
                                </th>
                                <th colSpan={2} className="py-2 px-2 font-medium text-center">
                                    {t('revenueInvoiced')}
                                </th>
                                <th colSpan={2} className="py-2 pl-2 font-medium text-center">
                                    {t('revenuePaid')}
                                </th>
                            </tr>
                            <tr className="text-zinc-500 border-b border-zinc-200">
                                <th className="py-2 px-2 font-medium text-right">{t('revenueMembership')}</th>
                                <th className="py-2 px-2 font-medium text-right">{t('revenueOther')}</th>
                                <th className="py-2 px-2 font-medium text-right">{t('revenueMembership')}</th>
                                <th className="py-2 pl-2 font-medium text-right">{t('revenueOther')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {months.map(m => {
                                const id = `${m.year}-${m.month}`;
                                const isOpen = open.has(id);
                                return (
                                    <Fragment key={id}>
                                        <tr className="border-b border-zinc-100">
                                            <td className="py-2 pr-4">
                                                <button
                                                    type="button"
                                                    className="flex items-center gap-2 text-left"
                                                    aria-expanded={isOpen}
                                                    aria-label={`${monthName(m)}: ${t('revenueTypeSplit')}`}
                                                    onClick={() => toggle(id)}
                                                >
                                                    <span aria-hidden>{isOpen ? '▾' : '▸'}</span>
                                                    {monthName(m)}
                                                </button>
                                            </td>
                                            <td className="py-2 px-2 text-right">{formatChf(m.invoicedMembership)}</td>
                                            <td className="py-2 px-2 text-right">{formatChf(m.invoicedOther)}</td>
                                            <td className="py-2 px-2 text-right">{formatChf(m.paidMembership)}</td>
                                            <td className="py-2 pl-2 text-right">{formatChf(m.paidOther)}</td>
                                        </tr>
                                        {isOpen && (
                                            <tr className="text-xs text-zinc-500">
                                                <td className="pl-6 py-1 pr-4" />
                                                <td className="py-1 px-2 text-right" colSpan={2}>
                                                    {t('revenueSplitInvoiced')}
                                                </td>
                                                <td className="py-1 pl-2 text-right" colSpan={2}>
                                                    {t('revenueSplitPaid')}
                                                </td>
                                            </tr>
                                        )}
                                        {isOpen &&
                                            m.invoicedByType.map(row => (
                                                <tr
                                                    key={`${row.parent ?? ''}-${row.key}`}
                                                    className="border-b border-zinc-50 text-zinc-600"
                                                >
                                                    <td
                                                        className={`py-1 pr-4 ${row.parent ? 'pl-10 text-zinc-500' : 'pl-6'}`}
                                                    >
                                                        {typeLabel(row)}
                                                    </td>
                                                    <td className="py-1 px-2 text-right" colSpan={2}>
                                                        {formatChf(row.amount)}
                                                    </td>
                                                    <td className="py-1 pl-2 text-right" colSpan={2}>
                                                        {formatChf(findAmount(m.paidByType, row))}
                                                    </td>
                                                </tr>
                                            ))}
                                    </Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </Card>
    );
};
